import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  tableCellClasses,
  tableRowClasses,
} from '@mui/material';
import { type ReactNode } from 'react';

import { LoadingOverlay } from './LoadingOverlay';
import { NoRecordsOverlay, type NoRecordsOverlayCustomMessage } from './NoRecords';
import { type TablePaginationProps } from './useTableState';
import { ROWS_PER_PAGE_OPTIONS } from './utils';
import { isDefined, isTruthy } from '../../utils/types';

type NotableTableProps = {
  headerRow: ReactNode;
  isPending: boolean;
  children: ReactNode;
  variant?: 'default' | 'striped';
  noRecordsOverlayMessage?: NoRecordsOverlayCustomMessage;
} & (
  | {
      estimatedRowCount: number;
      pagination: TablePaginationProps;
    }
  | {
      /**
       * We require you to specifically decide not to use pagination by setting it to `undefined.
       * Most tables must use pagination, and APIs should always support pagination.
       * While you may have a specific UI case where you don't want to show pagination, it is
       * too easy to forget to wire it up.
       * Therefore, you are required to either define it or to set it explicitly to `undefined`, so that
       * your intention is clear, and not an oversight
       */
      pagination: undefined;
      estimatedRowCount?: number;
    }
);

export function NotableTable(props: NotableTableProps) {
  const {
    headerRow,
    isPending,
    children,
    estimatedRowCount,
    pagination,
    variant,
    noRecordsOverlayMessage,
  } = props;

  return (
    <TableContainer component={Paper}>
      <Table stickyHeader size="small">
        <TableHead
          sx={{
            [`& .${tableCellClasses.root}`]: {
              backgroundColor: '#f7f7f7',
              fontWeight: 'bold',
            },
          }}
        >
          {headerRow}
        </TableHead>
        <TableBody
          sx={{
            ...(variant === 'striped' && {
              [`& .${tableRowClasses.root}`]: {
                '&:nth-of-type(odd)': {
                  backgroundColor: '#f6f6f6',
                  '&:hover': {
                    backgroundColor: '#f1f1f1',
                  },
                },
                '&:nth-of-type(even)': {
                  backgroundColor: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#f1f1f1',
                  },
                },
              },
            }),
          }}
        >
          {isPending || !isTruthy(estimatedRowCount) ? (
            <TableRow>
              <TableCell colSpan={1000}>
                {isPending ? (
                  <LoadingOverlay />
                ) : (
                  <NoRecordsOverlay customMessage={noRecordsOverlayMessage} />
                )}
              </TableCell>
            </TableRow>
          ) : (
            children
          )}
        </TableBody>
        {isDefined(pagination) && isDefined(estimatedRowCount) && (
          <TableFooter>
            <TableRow>
              <TablePagination
                page={pagination.paginationModel.page}
                count={estimatedRowCount}
                rowsPerPage={pagination.paginationModel.pageSize}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                onPageChange={(_event, newPage) => {
                  pagination.setPaginationModel((prev) => ({ ...prev, page: newPage }));
                }}
                onRowsPerPageChange={(event) => {
                  pagination.setPaginationModel({ page: 0, pageSize: Number(event.target.value) });
                }}
                sx={{
                  /**
                   * Bootstrap global styles set margin for p elements.
                   * This is a temporary workaround to remove the margin. Remove bootstrap and refactor all p element layout to Typography.
                   */
                  '& .MuiTablePagination-selectLabel': {
                    margin: 'unset',
                  },
                  '& .MuiTablePagination-displayedRows': {
                    margin: 'unset',
                  },
                }}
              />
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );
}
